import { localUrl as appLocalUrl, assetUrl as appAssetUrl } from '../app-path.js';
import slugify from 'slugify';
import geolocationInstructionsModal from './geolocation-instructions-modal';
import { trackGeolocationResult } from './geolocation-metrics';
import {
	getGenericData,
	SearchedStorage,
} from './local-storage-functions';
import LocalStorageHelper from './local-storage-helper';
import FavoriteCityService from './favorite-city-service';
import {
	isFavoriteButtonLoading,
	startFavoriteButtonLoading,
} from './favorite-button-loading';

const Elements = {
	desktopAutocomplete: $('#desktopSearchAutocomplete'),
	mobileAutocomplete: $('#mobileSearchAutocomplete'),
	mobileSearchButton: $('#bt_modalSearch_mobile'),
	desktopInput: $('#searchGeneral'),
	mobileInput: $('#mobileSearchInput'),
	mobileUl: $('#mobileAutocompleteSuggestions'),
	desktopUl: $('#autocompleteSuggestions'),
	mobileHeader: $('#mobileAutocompleteHeader'),
	desktopHeader: $('#autocompleteHeader'),
	mobileEmptyMessage: $('#mobileAutocompleteEmptyHistory'),
	desktopEmptyMessage: $('#autocompleteEmptyHistory'),
	mobileClearButton: $('#mobileAutocompleteHeaderClearButton'),
	desktopClearButton: $('#autocompleteHeaderClearButton'),
	myLocationButton: $('#autocompleteSearchMyLocation'),
	mobileLocationButton: $(
		'#mobileSearchAutocomplete #autocompleteSearchMyLocation',
	),
	desktopCloseButton: $('#desktopSearchAutocomplete .autocomplete-close'),
	mobileCloseButton: $('#mobileSearchAutocomplete .autocomplete-close'),
	desktopSpinner: $('#searchGeneralSpinner'),
	mobileSpinner: $('#mobileSearchInputSpinner'),
	desktopClearInputButton: $('#searchGeneralClear'),
	mobileClearInputButton: $('#mobileSearchInputClear'),

	getUl() {
		return this.getIsDesktop() ? this.desktopUl : this.mobileUl;
	},
	getHeader() {
		return this.getIsDesktop() ? this.desktopHeader : this.mobileHeader;
	},
	getEmptyMessage() {
		return this.getIsDesktop()
			? this.desktopEmptyMessage
			: this.mobileEmptyMessage;
	},
	getClearButton() {
		return this.getIsDesktop()
			? this.desktopClearButton
			: this.mobileClearButton;
	},
	getInput() {
		return this.getIsDesktop() ? this.desktopInput : this.mobileInput;
	},
	getAutocomplete() {
		return this.getIsDesktop()
			? this.desktopAutocomplete
			: this.mobileAutocomplete;
	},
	getIsDesktop() {
		return $(window).outerWidth() > 1199;
	},
};

const LOCATION_BUTTON_IDS = {
	use: 'Menu_MainSearch_Button_UseLocation',
	location: 'Menu_MainSearch_Button_Location',
};

export const keyByType = { city: 'city', beach: 'beach', airport: 'airport' };
export const idKeyByType = {
	city: 'idcity',
	beach: 'idbeach',
	airport: 'idairport',
};
export const pathByType = {
	city: 'cidade',
	beach: 'litoral',
	airport: 'aeroporto',
};
export const titleByType = {
	city: 'Cidades',
	beach: 'Praias',
	airport: 'Aeroportos',
};

const AUTOCOMPLETE_INIT_KEY = '__ctAutocompleteV2Initialized';
const SUPPRESS_MAIN_SEARCH_TRACKING_ATTR =
	'data-suppress-menu-main-search-tracking';
let isAutocompleteInitialized = false;
let currentSuggestions = [];
let isDesktopView = Elements.getIsDesktop();
let isSearchOpen = false;
const latestRequestIdByInput = {
	searchGeneral: 0,
	mobileSearchInput: 0,
};

function getInputUiElements($input) {
	if (!$input || !$input.length) return {};
	const inputId = $input.attr('id');
	if (inputId === 'searchGeneral') {
		return {
			spinner: Elements.desktopSpinner,
			clearButton: Elements.desktopClearInputButton,
		};
	}
	return {
		spinner: Elements.mobileSpinner,
		clearButton: Elements.mobileClearInputButton,
	};
}

function updateInputActionVisibility($input, isLoading = false) {
	if (!$input || !$input.length) return;

	const value = ($input.val() || '').toString().trim();
	const { spinner, clearButton } = getInputUiElements($input);
	const hasText = value.length > 0;

	if (clearButton?.length) {
		clearButton.toggleClass('is-visible', hasText);
	}

	if (spinner?.length) {
		spinner.toggleClass('is-visible', hasText && isLoading);
	}
}

function cancelPendingInputRequest($input) {
	if (!$input || !$input.length) return;
	const inputId = $input.attr('id');
	latestRequestIdByInput[inputId] = (latestRequestIdByInput[inputId] || 0) + 1;
}

function setupInputClearButtons() {
	const bindClearButton = ($button, $input, isDesktopInput) => {
		if (!$button.length || !$input.length) return;

		$button.off('click.clearInput').on('click.clearInput', event => {
			event.preventDefault();
			cancelPendingInputRequest($input);
			$input.val('');
			updateInputActionVisibility($input, false);

			const { ul, header, emptyMessage } = getContextByInput($input);
			ul.hide().empty();
			emptyMessage.css('display', 'flex');
			header.css('display', 'flex');
			renderSearchHistory(isDesktopInput);
			$input.trigger('focus');
		});
	};

	bindClearButton(
		Elements.desktopClearInputButton,
		Elements.desktopInput,
		true,
	);
	bindClearButton(Elements.mobileClearInputButton, Elements.mobileInput, false);
}

function blurInputs() {
	Elements.desktopInput.add(Elements.mobileInput).trigger('blur');
}

function isDesktopInputElement($input) {
	if (!$input || !$input.length) return Elements.getIsDesktop();
	return $input.attr('id') === 'searchGeneral';
}

function resolveAutocompleteContainer($input) {
	if (!$input || !$input.length) return Elements.getAutocomplete();

	const inputId = $input.attr('id');
	if (inputId === 'searchGeneral') {
		const $form = $input.closest('form');
		const $found = $form.find('#desktopSearchAutocomplete');
		return $found.length ? $found : Elements.desktopAutocomplete;
	}

	if (inputId === 'mobileSearchInput') {
		const $found = $input.closest('.autocomplete');
		return $found.length ? $found : Elements.mobileAutocomplete;
	}

	return Elements.getAutocomplete();
}

function selectContextElement($autocomplete, selector, fallback) {
	const $found = $autocomplete.find(selector);
	return $found.length ? $found : fallback;
}

function getContextByInput($input) {
	const isDesktopInput = isDesktopInputElement($input);
	const autocomplete = resolveAutocompleteContainer($input);
	const input =
		$input && $input.length
			? $input
			: isDesktopInput
				? Elements.desktopInput
				: Elements.mobileInput;
	const ulSelector = isDesktopInput
		? '#autocompleteSuggestions'
		: '#mobileAutocompleteSuggestions';
	const headerSelector = isDesktopInput
		? '#autocompleteHeader'
		: '#mobileAutocompleteHeader';
	const emptySelector = isDesktopInput
		? '#autocompleteEmptyHistory'
		: '#mobileAutocompleteEmptyHistory';
	const clearSelector = isDesktopInput
		? '#autocompleteHeaderClearButton'
		: '#mobileAutocompleteHeaderClearButton';

	return {
		isDesktopInput,
		autocomplete,
		input,
		ul: selectContextElement(
			autocomplete,
			ulSelector,
			isDesktopInput ? Elements.desktopUl : Elements.mobileUl,
		),
		header: selectContextElement(
			autocomplete,
			headerSelector,
			isDesktopInput ? Elements.desktopHeader : Elements.mobileHeader,
		),
		emptyMessage: selectContextElement(
			autocomplete,
			emptySelector,
			isDesktopInput
				? Elements.desktopEmptyMessage
				: Elements.mobileEmptyMessage,
		),
		clearButton: selectContextElement(
			autocomplete,
			clearSelector,
			isDesktopInput ? Elements.desktopClearButton : Elements.mobileClearButton,
		),
	};
}

export function autocomplete() {
	const hasGlobalInit =
		typeof window !== 'undefined' && window[AUTOCOMPLETE_INIT_KEY];
	if (isAutocompleteInitialized || hasGlobalInit) return;
	isAutocompleteInitialized = true;
	if (typeof window !== 'undefined') {
		window[AUTOCOMPLETE_INIT_KEY] = true;
	}

	resizeEventHandler();
	searchButtonEventHandler();
	setupInputListener();
	setupDesktopClickListener();
	setupClickOutsideListener();
	renderSearchHistory();
	setupUseLocationButtonListener();
	setupCloseButtons();
	setupClearHistoryButtonListener();
	setupFavoriteChangeListener();
	FavoriteCityService.refreshServerFavorites().then(() => renderSearchHistory());
	initializeMyLocationLabel();
	openSearchWhenComingFromPush();
}

function resizeEventHandler() {
	let resizeTimeout;
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => {
			const wasPreviouslyDesktop = isDesktopView;
			isDesktopView = Elements.getIsDesktop();

			if (wasPreviouslyDesktop !== isDesktopView) {
				Elements.desktopAutocomplete.hide();
				Elements.mobileAutocomplete.hide();
				Elements.mobileSearchButton.attr('aria-expanded', 'false');
				removeInputListeners();
				setupInputListener();
				setupDesktopClickListener();
				renderSearchHistory();
				setupUseLocationButtonListener();
			}
		}, 50);
	});
}

function searchButtonEventHandler() {
	const searchButton = Elements.mobileSearchButton;

	searchButton.on('click', e => {
		const wrapper = $('[data-id="mobile-search"]');
		if (wrapper.length) {
			e.preventDefault();
			e.stopPropagation();

			const isWrapperVisible = wrapper.is(':visible');
			if (isWrapperVisible) {
				Elements.mobileAutocomplete.hide();
				wrapper.stop(true, true).slideUp('fast');
				searchButton.attr('aria-expanded', 'false');
				Elements.getInput().attr('aria-expanded', 'false');

				if (isSearchOpen) {
					pushOnDataLayer('close_element', 'Menu_MainSearch');
				}
				blurInputs();
				isSearchOpen = false;
				return;
			}

			wrapper.stop(true, true).slideDown('fast');
			Elements.mobileAutocomplete.css('display', 'flex');
			Elements.getInput().attr('aria-expanded', 'true').trigger('focus');
			searchButton.attr('aria-expanded', 'true');
			if (!isSearchOpen) {
				pushOnDataLayer('open_element', 'Menu_MainSearch');
			}
			isSearchOpen = true;
			return;
		}
		const container = Elements.getAutocomplete();
		const isVisible = container.css('display') === 'flex';
		container.css('display', isVisible ? 'none' : 'flex');
		searchButton.attr('aria-expanded', isVisible ? 'false' : 'true');

		if (!isVisible) {
			Elements.getInput().attr('aria-expanded', 'true').trigger('focus');
			if (!isSearchOpen) {
				pushOnDataLayer('open_element', 'Menu_MainSearch');
			}
			isSearchOpen = true;

			return;
		}

		Elements.getInput().attr('aria-expanded', 'false');
		if (isSearchOpen) {
			pushOnDataLayer('close_element', 'Menu_MainSearch');
		}
		blurInputs();
		isSearchOpen = false;
	});
}

function setupInputListener() {
	const handler = createDebouncedInputHandler();
	const shouldSuppressMainSearchTracking = $input => {
		const input = $input && $input[0];
		if (!input || !input.hasAttribute(SUPPRESS_MAIN_SEARCH_TRACKING_ATTR))
			return false;

		input.removeAttribute(SUPPRESS_MAIN_SEARCH_TRACKING_ATTR);
		return true;
	};

	const onFocus = function () {
		const $input = $(this);
		const { autocomplete } = getContextByInput($input);
		if (!autocomplete.length) return;
		const isVisible = autocomplete.is(':visible');

		if (!isVisible) {
			autocomplete.css('display', 'flex');
		}

		$input.attr('aria-expanded', 'true');

		if (shouldSuppressMainSearchTracking($input)) {
			isSearchOpen = true;
			return;
		}

		if (!isSearchOpen) {
			pushOnDataLayer('open_element', 'Menu_MainSearch');
			isSearchOpen = true;
		}
	};

	Elements.desktopInput.on('input', handler);
	Elements.mobileInput.on('input', handler);
	Elements.desktopInput.on('input', function () {
		updateInputActionVisibility($(this), false);
	});
	Elements.mobileInput.on('input', function () {
		updateInputActionVisibility($(this), false);
	});
	Elements.desktopInput.on('focus', onFocus);
	Elements.mobileInput.on('focus', onFocus);
	setupInputClearButtons();
	updateInputActionVisibility(Elements.desktopInput, false);
	updateInputActionVisibility(Elements.mobileInput, false);
}

function removeInputListeners() {
	Elements.desktopInput.off('input click focus');
	Elements.mobileInput.off('input focus');
}

function createDebouncedInputHandler() {
	return debounce(async function () {
		const $input = $(this);
		const inputValue = $input.val()?.trim();
		const inputId = $input.attr('id');
		const { isDesktopInput, ul, header, emptyMessage } =
			getContextByInput($input);

		if (!inputValue) {
			cancelPendingInputRequest($input);
			updateInputActionVisibility($input, false);
			ul.hide().empty();
			emptyMessage.css('display', 'flex');
			header.css('display', 'flex');
			renderSearchHistory(isDesktopInput);
			return;
		}

		if (inputValue.length < 3) {
			cancelPendingInputRequest($input);
			updateInputActionVisibility($input, false);
			return;
		}

		const requestId = (latestRequestIdByInput[inputId] || 0) + 1;
		latestRequestIdByInput[inputId] = requestId;
		updateInputActionVisibility($input, true);

		try {
			const response = await $.ajax({
				url: appLocalUrl('/json/busca-por-nome'),
				type: 'POST',
				dataType: 'json',
				cache: true,
				data: { name: inputValue },
			});

			if (requestId !== latestRequestIdByInput[inputId]) return;
			const latestInputValue = ($input.val() || '').toString().trim();
			if (latestInputValue !== inputValue) return;

			treatResponse(response, isDesktopInput);
		} catch (error) {
			if (requestId !== latestRequestIdByInput[inputId]) return;
			console.error('Erro na busca:', error);
		} finally {
			if (requestId === latestRequestIdByInput[inputId]) {
				updateInputActionVisibility($input, false);
			}
		}
	}, 300);
}

function treatResponse(response, isDesktopInput) {
	const validTypes = ['city', 'beach', 'airport'];

	const sliced = response
		.map(({ response: { data }, type }) => {
			const normalizedType = type?.toLowerCase();
			return {
				places: Array.isArray(data) ? data.slice(0, 5) : [],
				type: normalizedType,
			};
		})
		.filter(({ type }) => validTypes.includes(type));

	currentSuggestions = sliced;
	renderSuggestions(sliced, isDesktopInput);
}

function renderSearchHistory(targetIsDesktop = null) {
	const history = SearchedStorage.get();
	const favorites = FavoriteCityService.getVisibleFavorites();
	const normalizedHistory = Array.isArray(history)
		? [...history].reverse()
		: [];
	const contexts = [];

	if (targetIsDesktop === null) {
		contexts.push({
			ul: Elements.desktopUl,
			header: Elements.desktopHeader,
			empty: Elements.desktopEmptyMessage,
			clearButton: Elements.desktopClearButton,
		});
		contexts.push({
			ul: Elements.mobileUl,
			header: Elements.mobileHeader,
			empty: Elements.mobileEmptyMessage,
			clearButton: Elements.mobileClearButton,
		});
	} else {
		contexts.push({
			ul: targetIsDesktop ? Elements.desktopUl : Elements.mobileUl,
			header: targetIsDesktop ? Elements.desktopHeader : Elements.mobileHeader,
			empty: targetIsDesktop
				? Elements.desktopEmptyMessage
				: Elements.mobileEmptyMessage,
			clearButton: targetIsDesktop
				? Elements.desktopClearButton
				: Elements.mobileClearButton,
		});
	}

	contexts.forEach(({ ul, header, empty, clearButton }) => {
		if (!ul.length) return;

		ul.empty();
		if (!normalizedHistory.length) {
			header.hide();
			empty.css('display', 'flex');
			ul.show();
			if (clearButton?.length) clearButton.hide();
			// if (Array.isArray(favorites) && favorites.length) {
			// 	appendFavoritesSection(ul, favorites);
			// }
			return;
		}

		header.show();
		empty.hide();
		ul.show();
		if (clearButton?.length) clearButton.show();

		let index = 0;

		normalizedHistory.forEach(raw => {
			const isValidType = Object.keys(keyByType).some(
				type => raw?.[keyByType[type]],
			);
			const type = Object.keys(keyByType).find(type => raw?.[keyByType[type]]);

			if (!isValidType) return;

			index++;

			const cityData = raw[keyByType[type]];
			const place = LocalStorageHelper.unmapCityData(cityData, type);
			const meta = buildCityMeta(place, keyByType[type]);
			const favButton = createFavoriteButton(cityData, type);

			const li = createListItem({
				label: meta.label,
				href: meta.href,
				favButton,
				index,
				cityCid: cityData.cloc || cityData.cid,
				isHistory: true,
			});

			ul.append(li);
		});
	});
}

function setupFavoriteChangeListener() {
	try {
		const refreshHistory = () => {
			const desktopValue = (Elements.desktopInput.val() || '')
				.toString()
				.trim();
			const mobileValue = (Elements.mobileInput.val() || '').toString().trim();
			const hasQuery = desktopValue.length >= 3 || mobileValue.length >= 3;
			if (!hasQuery) {
				renderSearchHistory();
			}
		};
		document.addEventListener('favoriteChanged', refreshHistory);
		document.addEventListener('favoriteSync', refreshHistory);
	} catch (e) {}
}

function renderSuggestions(
	suggestions,
	isDesktopInput = Elements.getIsDesktop(),
) {
	const ul = isDesktopInput ? Elements.desktopUl : Elements.mobileUl;
	const header = isDesktopInput
		? Elements.desktopHeader
		: Elements.mobileHeader;
	const empty = isDesktopInput
		? Elements.desktopEmptyMessage
		: Elements.mobileEmptyMessage;

	ul.empty().show();
	header.hide();
	empty.hide();

	let index = 0;

	suggestions.forEach(({ places, type }) => {
		if (!places.length) return;

		ul.append($('<p>').text(titleByType[type]));

		places.forEach(place => {
			index++;
			const meta = buildCityMeta(place, type);
			const cityData = LocalStorageHelper.mapCityData(place);
			const favButton = createFavoriteButton(cityData, type);
			const genericData = getGenericData('search');
			const data = {
				...genericData,
				[keyByType[type]]: cityData,
			};

			const a = $('<a>')
				.attr('href', meta.href)
				.on('click', () => {
					SearchedStorage.append(data, 10, 'cid', keyByType[type]);
				});

			const li = createListItem({
				label: meta.label,
				href: meta.href,
				favButton,
				linkElement: a,
				index,
				cityCid: cityData.cloc || cityData.cid,
			});

			ul.append(li);
		});
	});

	if (isDesktopInput) {
		ul.css('padding-top', '8px');
	} else {
		ul.css('padding-top', '0');
	}
}

function buildCityMeta(place, type) {
	const name = place[keyByType[type]];

	const uf = LocalStorageHelper.resolveLocationCode(place);
	const label = `${name} - ${uf}`;
	const slug = slugify(String(name)).replaceAll('-', '').toLowerCase();
	const href = appLocalUrl(`/previsao-do-tempo/${pathByType[type]}/${place[idKeyByType[type]]}/${slug}-${uf.toLowerCase()}`);
	return { name, uf, label, href };
}

function isSameFavoriteCity(firstCity = {}, secondCity = {}) {
	const firstId = FavoriteCityService.getCityStorageId(firstCity);
	const secondId = FavoriteCityService.getCityStorageId(secondCity);

	return Boolean(firstId && secondId && String(firstId) === String(secondId));
}

export async function toggleFavorite(city, elementId, type, button = null) {
	if (isFavoriteButtonLoading(button)) {
		return {
			status: 'pending',
			isFavorited: FavoriteCityService.isCityFavorited(city),
		};
	}

	const dataLayerCityData = {
		type: type,
		el_id: city.cid,
		el_name: city.label,
	};
	const action = FavoriteCityService.isCityFavorited(city) ? 'remove' : 'add';
	const stopLoading = startFavoriteButtonLoading(button, action);

	try {
		const result = await FavoriteCityService.toggleFavorite(city);

		if (result.status === 'removed') {
			pushOnDataLayer('remove_favorite', elementId, dataLayerCityData);
		} else if (
			result.status === 'added' ||
			result.status === 'already_exists'
		) {
			pushOnDataLayer('add_favorite', elementId, dataLayerCityData);
		} else if (result.status === 'auth_required') {
			pushOnDataLayer(
				'favorite_login_required',
				elementId,
				dataLayerCityData,
			);
		} else if (result.status === 'limit_reached') {
			pushOnDataLayer(
				'favorite_limit_reached',
				elementId,
				dataLayerCityData,
			);
		}

		return result;
	} finally {
		stopLoading();
	}
}

function createFavoriteButton(city, type) {
	const button = $('<button type="button" class="historyFavButton"></button>');
	const icon = $('<img>').attr('alt', 'Ícone de favorito');
	button.append(icon);

	button.attr('data-cid', city.cid);

	function updateVisual(isFavorited) {
		const iconName = isFavorited ? 'star-filled' : 'star-empty';
		const title = isFavorited ? 'Remover dos favoritos' : 'Favoritar cidade';
		icon.attr('src', appAssetUrl(`/dist/images/v2/svg/${iconName}.svg`));
		button.attr({ title, 'aria-label': title });
	}

	updateVisual(FavoriteCityService.isCityFavorited(city));

	button.on('click', async e => {
		e.stopPropagation();
		await toggleFavorite(
			city,
			'Menu_MainSearch_Button_Star',
			type,
			button[0],
		);
	});

	document.addEventListener('favoriteChanged', event => {
		if (isSameFavoriteCity(event.detail.city, city)) {
			updateVisual(event.detail.isFavorited);
		}
	});
	document.addEventListener('favoriteSync', () => {
		updateVisual(FavoriteCityService.isCityFavorited(city));
	});

	return button;
}

function pushOnDataLayer(eventName, id, additionalData = {}) {
	window.dataLayer = window.dataLayer || [];

	window.dataLayer.push({
		event: eventName,
		id: id,
		...additionalData,
	});
}

function createListItem({
	label,
	href,
	icon = null,
	favButton = null,
	linkElement = null,
	index = 0,
	cityCid,
	isHistory = false,
}) {
	const li = $(
		'<li class="autocomplete-item" role="option" aria-selected="false"></li>',
	);
	const wrapper = $('<div class="autocomplete-content"></div>');

	const eventIndex = String(index).padStart(2, '0');
	const getIsItemFavorited = () =>
		FavoriteCityService.isCityFavorited({ cid: cityCid, cloc: cityCid });
	const resolveDataId = isFavorited => {
		if (isHistory) {
			return `Menu_MainSearch_${isFavorited ? 'FavoriteItem' : 'Item'}_RecentLink-${eventIndex}`;
		}
		return `Menu_MainSearch_${isFavorited ? 'FavoriteItem' : 'Item'}_NavLink-${eventIndex}`;
	};
	const dataId = resolveDataId(getIsItemFavorited());

	const a =
		linkElement?.attr('data-id', dataId) ||
		$('<a></a>').attr('href', href).attr('data-id', dataId);

	a.append($('<span></span>').text(label));
	if (icon) a.prepend(icon);

	const contentWrapper = $('<div></div>').append(a);
	wrapper.append(contentWrapper);
	if (favButton) wrapper.append(favButton);
	li.append(wrapper);

	document.addEventListener('favoriteChanged', event => {
		const updatedCity = event?.detail?.city || {};
		const updatedId = updatedCity.cloc || updatedCity.cid;
		if (String(updatedId) !== String(cityCid)) return;
		a.attr('data-id', resolveDataId(event.detail.isFavorited));
	});

	return li;
}

function debounce(fn, delay) {
	let timeout;
	return function (...args) {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn.apply(this, args), delay);
	};
}

function setupDesktopClickListener() {
	const input = Elements.desktopInput;

	input.on('click', e => {
		e.stopPropagation();
		const $input = $(e.currentTarget);
		const { autocomplete } = getContextByInput($input);
		if (autocomplete.length) {
			autocomplete.css('display', 'flex');
		}
		$input.attr('aria-expanded', 'true');
		if (!isSearchOpen) {
			pushOnDataLayer('open_element', 'Menu_MainSearch');
		}
		isSearchOpen = true;
	});
}

function setupClickOutsideListener() {
	$(document)
		.off('click.autocompletev2')
		.on('click.autocompletev2', e => {
			const container = Elements.desktopAutocomplete;
			const input = Elements.desktopInput;
			const mobileWrapper = $('[data-id="mobile-search"]');
			const mobileToggle = Elements.mobileSearchButton;
			const mobileContainer = Elements.mobileAutocomplete;
			const isThirtyDaysInteraction =
				$(e.target).closest(
					'[data-thirty-days-modal-overlay], [data-thirty-days-modal-trigger]',
				).length > 0;

			if (isThirtyDaysInteraction) return;

			const clickedToggle =
				mobileToggle.is(e.target) || mobileToggle.has(e.target).length > 0;
			let didClose = false;

			const isDesktopVisible = container.is(':visible');
			if (
				isDesktopVisible &&
				!container.is(e.target) &&
				container.has(e.target).length === 0 &&
				!input.is(e.target) &&
				!clickedToggle
			) {
				container.hide();
				input.attr('aria-expanded', 'false');
				didClose = true;
			}

			if (!Elements.getIsDesktop()) {
				const clickedInsideMobile =
					mobileContainer.is(e.target) ||
					mobileContainer.has(e.target).length > 0;
				const isMobileVisible = mobileContainer.is(':visible');
				const isMobileExpanded =
					mobileToggle.attr('aria-expanded') === 'true' ||
					Elements.mobileInput.attr('aria-expanded') === 'true';
				if (
					!clickedInsideMobile &&
					!clickedToggle &&
					isMobileVisible &&
					isMobileExpanded
				) {
					if (mobileWrapper.length && mobileWrapper.is(':visible')) {
						mobileWrapper.stop(true, true).slideUp('fast');
					}
					mobileContainer.hide();
					Elements.mobileSearchButton.attr('aria-expanded', 'false');
					Elements.mobileInput.attr('aria-expanded', 'false');
					didClose = true;
				}
			}

			if (didClose) {
				if (isSearchOpen) {
					pushOnDataLayer('close_element', 'Menu_MainSearch');
				}
				blurInputs();
				isSearchOpen = false;
			}
		});
}

function setupUseLocationButtonListener() {
	const $targets = Elements.myLocationButton.add(Elements.mobileLocationButton);
	const resolveButtonId = async () => {
		try {
			if ('permissions' in navigator) {
				const status = await navigator.permissions.query({
					name: 'geolocation',
				});
				if (status?.state === 'granted') return LOCATION_BUTTON_IDS.location;
			}
		} catch (err) {}
		return LOCATION_BUTTON_IDS.use;
	};

	resolveButtonId().then(id => {
		try {
			$targets.attr('data-id', id);
		} catch (e) {}
	});

	try {
		$targets.each(function () {
			const $btn = $(this);
			if (!$btn.data('original-html')) {
				$btn.data('original-html', $btn.html());
			}
		});
	} catch (e) {}

	const callback = async e => {
		e.preventDefault();
		e.stopPropagation();
		const id = await resolveButtonId();
		pushOnDataLayer('click_button', id);

		const $btn = $(e.currentTarget);

		try {
			if ('permissions' in navigator) {
				const status = await navigator.permissions.query({
					name: 'geolocation',
				});
				if (status?.state === 'denied') {
					trackGeolocationResult({
						method: id,
						status: 'denied',
					});
					geolocationInstructionsModal.open();
					return;
				}
			}
		} catch (err) {}

		if ('geolocation' in navigator) {
			try {
				$btn
					.empty()
					.attr('disabled', 'true')
					.append('<div class="loading"></div>');
			} catch (err) {}

			navigator.geolocation.getCurrentPosition(
				pos => {
					handleUserGeolocation(pos, id);
				},
				() => {
					try {
						const original = $btn.data('original-html');
						if (original) {
							$btn.removeAttr('disabled').html(original);
						}
					} catch (err) {}
					trackGeolocationResult({
						method: id,
						status: 'denied',
					});
					geolocationInstructionsModal.open();
				},
				{ enableHighAccuracy: true, maximumAge: 3000 },
			);
		} else {
			trackGeolocationResult({
				method: id,
				status: 'denied',
			});
			geolocationInstructionsModal.open();
		}
	};

	$targets.off('click').on('click', callback);
}

function setupCloseButtons() {
	const onClose = () => {
		const mobileWrapper = $('[data-id="mobile-search"]');
		const wasVisible =
			Elements.desktopAutocomplete.is(':visible') ||
			Elements.mobileAutocomplete.is(':visible') ||
			(mobileWrapper.length && mobileWrapper.is(':visible'));

		Elements.desktopAutocomplete.hide();
		Elements.mobileAutocomplete.hide();

		if (mobileWrapper.length && mobileWrapper.is(':visible')) {
			mobileWrapper.stop(true, true).slideUp('fast');
		}

		if (!Elements.getIsDesktop()) {
			Elements.mobileSearchButton.attr('aria-expanded', 'false');
		}
		Elements.getInput().attr('aria-expanded', 'false');
		if (wasVisible && isSearchOpen) {
			pushOnDataLayer('close_element', 'Menu_MainSearch');
		}
		blurInputs();
		isSearchOpen = false;
	};

	Elements.desktopCloseButton.off('click').on('click', onClose);
	Elements.mobileCloseButton.off('click').on('click', onClose);
}

async function handleUserGeolocation(
	geolocationPosition,
	methodId = LOCATION_BUTTON_IDS.use,
) {
	const { coords } = geolocationPosition;
	let tracked = false;

	try {
		const requestedCity = await $.ajax({
			url: appLocalUrl('/json/minha-localizacao'),
			type: 'POST',
			dataType: 'json',
			cache: true,
			context: this,
			data: { latitude: coords.latitude, longitude: coords.longitude },
			contentType: 'application/x-www-form-urlencoded',
		});

		const locationData = requestedCity?.data?.[0];
		const { city, uf, idcity } = locationData || {};
		if (city && uf && idcity) {
			trackGeolocationResult({
				method: methodId,
				status: 'granted',
				locationData,
				coords,
			});
			tracked = true;
			const slug = slugify(String(city)).replaceAll('-', '').toLowerCase();
			const href = appLocalUrl(`/previsao-do-tempo/cidade/${idcity}/${slug}-${uf.toLowerCase()}`);
			window.location = href;
			return;
		}
	} catch (e) {}

	if (!tracked) {
		trackGeolocationResult({
			method: methodId,
			status: 'granted',
			coords,
		});
	}
	redirectToSaoPaulo();
}

function redirectToSaoPaulo() {
	window.location = appLocalUrl('/previsao-do-tempo/cidade/558/saopaulo-sp');
}

function setupClearHistoryButtonListener() {
	const onClear = e => {
		e.preventDefault();
		e.stopPropagation();
		SearchedStorage.clear();

		renderSearchHistory();

		pushOnDataLayer('click_button', 'Menu_MainSearch_Button_DeleteHistory');

		const $target = $(e.currentTarget);
		const isDesktopButton = $target.is(Elements.desktopClearButton);
		const $input = isDesktopButton
			? Elements.desktopInput
			: Elements.mobileInput;
		if ($input.length) {
			$input.trigger('focus');
		}
	};

	Elements.mobileClearButton.off('click').on('click', onClear);
	Elements.desktopClearButton.off('click').on('click', onClear);
}

function initializeMyLocationLabel() {
	try {
		if (!('permissions' in navigator)) return;
		navigator.permissions.query({ name: 'geolocation' }).then(status => {
			if (status.state !== 'granted') return;
			if ('geolocation' in navigator) {
				navigator.geolocation.getCurrentPosition(async pos => {
					try {
						const requestedCity = await $.ajax({
							url: appLocalUrl('/json/minha-localizacao'),
							type: 'POST',
							dataType: 'json',
							cache: true,
							context: this,
							data: {
								latitude: pos.coords.latitude,
								longitude: pos.coords.longitude,
							},
							contentType: 'application/x-www-form-urlencoded',
						});
						const cityData = requestedCity?.data?.[0];
						if (!cityData) return;
						const uf = cityData.uf?.toUpperCase?.() || '';
						const label = `${cityData.city} - ${uf}`;
						Elements.myLocationButton.find('span').text(label);
						Elements.mobileLocationButton.find('span').text(label);
					} catch (e) {}
				});
			}
		});
	} catch (e) {}
}

function openSearchWhenComingFromPush() {
	if (!cameFromPushSource()) return;

	const wrapper = $('[data-id="mobile-search"]');
	if (wrapper.length) {
		wrapper.show();
	}

	const isDesktop = Elements.getIsDesktop();
	const container = isDesktop
		? Elements.desktopAutocomplete
		: Elements.mobileAutocomplete;

	container.css('display', 'flex');
	Elements.getInput().attr('aria-expanded', 'true');

	if (!isDesktop) {
		Elements.mobileSearchButton.attr('aria-expanded', 'true');
	}

	setTimeout(() => {
		Elements.getInput().trigger('focus');
	}, 0);

	pushOnDataLayer('open_element', 'Menu_MainSearch');
	isSearchOpen = true;
}

function cameFromPushSource() {
	try {
		const params = new URLSearchParams(window.location.search || '');
		const normalized = val => (val || '').toString().toLowerCase();
		const pushWords = [
			'push',
			'pushnews',
			'pushnotification',
			'push_notification',
		];

		const hasPushFlag = [
			'push',
			'frompush',
			'pushnotification',
			'push_notification',
		].some(key => {
			if (!params.has(key)) return false;
			const value = normalized(params.get(key));
			if (!value) return true;
			return (
				value === '1' ||
				value === 'true' ||
				value === 'yes' ||
				pushWords.some(word => value.includes(word))
			);
		});

		const utmSource = normalized(params.get('utm_source'));
		const utmMedium = normalized(params.get('utm_medium'));
		const utmCampaign = normalized(params.get('utm_campaign'));
		const hasPushUtm = [utmSource, utmMedium, utmCampaign].some(value =>
			pushWords.some(word => value.includes(word)),
		);

		return hasPushFlag || hasPushUtm;
	} catch (e) {
		return false;
	}
}
